import {
  CatalogProductDetailViewFragmentFragment,
  GetCatalogProductViewQuery,
} from '@notino/shared/definitions/types';
import { AdditionalServicesAvailability } from '@notino/web-tracking';
import { VariantSelector } from '@notino/web-tracking/dist/types/package/ga4/events/constants';

import {
  runTrackingEventViewItem,
  runExponeaFunctions,
} from '@containers/App/utils';
import { FeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { dispatchTrackingEvent } from '@context/tracking/utils';
import { ProductEventWither } from '@helpers/googleTagManager';
import { getGiftCampaigns } from '@lib/gift';

import {
  RRP_PRICE_PROMO_LABEL,
  MIN_PRICE_PROMO_LABEL,
} from './hooks/usePriceLogic/tracking';
import { getRenderableDiscoBoxSampleId } from './ProductBaseInfo/DiscoveryBoxSelfChoice';

type Product = NonNullable<
  GetCatalogProductViewQuery['productDetailByCatalogMasterId']
>;

interface ITrackPageReloadParams {
  variant: Product['variants'][number];
  product: Product;
  engravingAvailable: AdditionalServicesAvailability;
  tryItFirstAvailable: AdditionalServicesAvailability;
  rrpShown: boolean;
  minimalPriceShown: boolean;
  skinAnalyzer: boolean;
  hairAnalyzer: boolean;
  trackingDisplayType?: VariantSelector;
  featureFlags: FeatureFlags;
  hasVoucher?: boolean;
}
export const trackPageReload = ({
  variant,
  product,
  engravingAvailable,
  tryItFirstAvailable,
  rrpShown,
  minimalPriceShown,
  trackingDisplayType,
  skinAnalyzer,
  hairAnalyzer,
  featureFlags,
  hasVoucher,
}: ITrackPageReloadParams) => {
  // We need to use setTimeout for perf. optim. of hydration
  // This event is often called with 'page_view' event
  // They are often called in separate `useEffect` hooks
  // To make sure this event is logged after 'page_view' event
  // We need to use setTimeout, just to track this event a little later

  setTimeout(() => {
    dispatchTrackingEvent({
      event: 'view_item',
      variant_selector: trackingDisplayType,
      ...(hasVoucher && {
        variant_pricing: 'not_highlighted',
      }),
      products: [
        ProductEventWither()
          .withProduct(product)
          .withVariant(variant)
          .withDiscountPrices(variant.attributes)
          .withAdditionalPromoLabels(
            [
              rrpShown ? RRP_PRICE_PROMO_LABEL : '',
              minimalPriceShown ? MIN_PRICE_PROMO_LABEL : '',
            ].filter(Boolean)
          )
          .withServices({
            engravingAvailable,
            tryItFirstAvailable,
            skinAnalyzer,
            hairAnalyzer,
            discoveryBoxAvailable: Boolean(
              getRenderableDiscoBoxSampleId(
                product,
                variant,
                featureFlags as Pick<
                  FeatureFlags,
                  'discovery_box_self_choice_enabled'
                >
              )
            ),
          })
          .build(),
      ],
      _clear: true,
    });
  });
};

export const trackItemInExponea = (
  product: CatalogProductDetailViewFragmentFragment,
  variant: CatalogProductDetailViewFragmentFragment['variants'][number]
) => {
  runTrackingEventViewItem({
    id: variant.webId,
    name: variant.name ?? '',
    code: variant.productCode,
    masterProductCode: product.masterProductCode,
    brandName: product.brand?.name ?? '',
    gender: variant.gender ?? '',
    marketType: product.brand?.market ?? '',
    price: variant.price.value ?? 0,
    kind: product.kind ?? undefined,
    categories: product.category?.filter((c): c is string => c != null) ?? [],
    subcategories:
      product.subCategory?.filter((c): c is string => c != null) ?? [],
    types: product.type?.filter((c): c is string => c != null) ?? [],
    stockQuantity: variant.stockAvailability.code ?? '',
    attributes: variant.attributes,
    isIndividual: product.variants.length === 1,
    size: variant.parameters?.amount ?? undefined,
    sizeUnit: product.variants[0]?.parameters?.unit ?? undefined,
    navigationValues: variant.navigationValues,
    giftIncluded: getGiftCampaigns({ product, variant } as any)
      .hasGiftCampaigns,
  });
  runExponeaFunctions();
};
